import React from 'react';
import { youthLeaders, adultMentors, communityMembers } from '../mockData';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { GraduationCap, Award, Users } from 'lucide-react';

const Leadership = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-16 md:py-24" style={{ backgroundColor: '#dbbf5720' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#7e2f22' }}>
              Our Leadership Team
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              Meet the inspiring young women and dedicated mentors leading Naiwa Daughters into the future.
            </p>
          </div>
        </div>
      </section>

      {/* Youth Leaders */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full mb-4" style={{ backgroundColor: '#53a5ca20' }}>
                <Users className="h-5 w-5" style={{ color: '#53a5ca' }} />
                <span className="font-semibold" style={{ color: '#53a5ca' }}>Youth Leadership</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#7e2f22' }}>Our Young Leaders</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Future leaders are leaders now, shaping the future of their communities today.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {youthLeaders.map((leader) => (
                <Card key={leader.id} className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={leader.image} 
                      alt={leader.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <h3 className="text-xl font-bold mb-1">{leader.name}</h3>
                      <p className="text-sm opacity-90">{leader.role}</p>
                    </div>
                  </div>
                  <CardContent className="pt-6">
                    <div className="flex items-center space-x-4 mb-4 text-sm text-gray-600">
                      <div className="flex items-center space-x-1">
                        <GraduationCap className="h-4 w-4" style={{ color: '#53a5ca' }} />
                        <span>{leader.school}</span>
                      </div>
                    </div>
                    <p className="text-gray-700 mb-4 text-sm leading-relaxed">{leader.bio}</p>
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Key Achievements</p>
                      <div className="flex flex-wrap gap-2">
                        {leader.achievements.map((achievement, idx) => (
                          <Badge 
                            key={idx} 
                            variant="secondary" 
                            className="text-xs"
                            style={{ backgroundColor: '#53a5ca20', color: '#53a5ca' }}
                          >
                            {achievement}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16" style={{ backgroundColor: '#53a5ca10' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full mb-4" style={{ backgroundColor: '#dbbf5720' }}>
                <Users className="h-5 w-5" style={{ color: '#dbbf57' }} />
                <span className="font-semibold" style={{ color: '#dbbf57' }}>Members</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#7e2f22' }}>Collective Members</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">These members stand alongside our youth leaders and contribute to ND success as they build their leadership skills to become future ND leaders.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {communityMembers.map((member) => (
                <Card key={member.id} className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 group" data-testid={`community-member-card-${member.id}`}>
                  <div className="relative h-72 overflow-hidden">
                    <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <h3 className="text-xl font-bold">{member.name}</h3>
                      <p className="text-sm opacity-90">{member.role}</p>
                    </div>
                    {member.hoverText ? (
                      <div className="absolute inset-0 bg-black/80 text-white p-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end" data-testid={`community-member-hover-text-${member.id}`}>
                        <p className="text-sm leading-relaxed">{member.hoverText}</p>
                      </div>
                    ) : null}
                  </div>
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-700 leading-relaxed">{member.bio}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Adult Mentors */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full mb-4" style={{ backgroundColor: '#e37c5520' }}>
                <Award className="h-5 w-5" style={{ color: '#e37c55' }} />
                <span className="font-semibold" style={{ color: '#e37c55' }}>Adult Mentors</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#7e2f22' }}>Our Dedicated Mentors</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Experienced leaders providing guidance, support, and wisdom to the next generation.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {adultMentors.map((mentor) => (
                <Card key={mentor.id} className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={mentor.image} 
                      alt={mentor.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <h3 className="text-xl font-bold mb-1">{mentor.name}</h3>
                      <p className="text-sm opacity-90">{mentor.role}</p>
                    </div>
                  </div>
                  <CardContent className="pt-6">
                    <p className="text-gray-700 mb-4 text-sm leading-relaxed">{mentor.bio}</p>
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Areas of Expertise</p>
                      <div className="flex flex-wrap gap-2">
                        {mentor.expertise.map((skill, idx) => (
                          <Badge 
                            key={idx} 
                            variant="secondary" 
                            className="text-xs"
                            style={{ backgroundColor: '#e37c5520', color: '#e37c55' }}
                          >
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="p-8 md:p-12 text-center" style={{ backgroundColor: '#7e2f22' }}>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Become a Mentor
                </h2>
                <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
                  Share your wisdom, experience, and passion with the next generation of Indigenous women leaders.
                </p>
                <a href="/mentor">
                  <button 
                    className="px-8 py-3 rounded-lg font-semibold text-lg transition-all duration-200 hover:opacity-90"
                    style={{ backgroundColor: '#dbbf57', color: '#7e2f22' }}
                    data-testid="leadership-become-mentor-button"
                  >
                    Apply to Become a Mentor
                  </button>
                </a>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Leadership;